/**
 * Recolouring helpers for the Illustrator-exported SVGs in ./assets.
 *
 * Those files keep their colours in one <style> block as `fill: <color>;`
 * declarations (hex, 3-digit hex or named colours such as `blue`), so a "slot"
 * is one distinct fill colour found there.
 *
 *   slot = { color: '#rrggbb' }   the original colour, normalised
 *
 * Recolouring is a single pass over the style block, so choosing a colour that
 * equals another slot's original colour cannot cascade.
 */

const STYLE_RE = /(<style[^>]*>)([\s\S]*?)(<\/style>)/i;
const FILL_RE = /(fill\s*:\s*)([^;}\s]+)/gi;
const IGNORED = /^(none|transparent|inherit|currentcolor|url\()/i;

let probeCtx = null;

// '#f2b' -> '#ff22bb', 'blue' -> '#0000ff'. Returns null for non-colours.
function toHex(token) {
    const c = token.trim().toLowerCase();
    if (!c || IGNORED.test(c)) return null;
    if (/^#[0-9a-f]{6}$/.test(c)) return c;
    if (/^#[0-9a-f]{3}$/.test(c)) return `#${c[1]}${c[1]}${c[2]}${c[2]}${c[3]}${c[3]}`;
    if (!probeCtx) probeCtx = document.createElement('canvas').getContext('2d');
    probeCtx.fillStyle = '#000000';
    probeCtx.fillStyle = c; // ignored by the browser if `c` is not a valid colour
    const out = probeCtx.fillStyle;
    return /^#[0-9a-f]{6}$/.test(out) ? out : null;
}

// Ordered, de-duplicated list of the fill colours used in the SVG.
export function parseSvgColors(svgText) {
    const style = svgText.match(STYLE_RE);
    if (!style) return [];
    const seen = new Set();
    const slots = [];
    style[2].replace(FILL_RE, (all, prefix, token) => {
        const hex = toHex(token);
        if (hex && !seen.has(hex)) {
            seen.add(hex);
            slots.push({ color: hex });
        }
        return all;
    });
    return slots;
}

// Chosen colour per slot, then `fallback[i]`, then the slot's original colour.
export function resolveColors(slots, chosen = [], fallback = []) {
    return slots.map((s, i) => chosen[i] || fallback[i] || s.color);
}

// Returns svgText with each slot's colour swapped for the matching `colors[i]`.
export function applyColors(svgText, slots, colors) {
    const map = {};
    slots.forEach((s, i) => {
        if (colors[i]) map[s.color] = colors[i];
    });
    return svgText.replace(STYLE_RE, (all, open, css, close) => {
        const next = css.replace(FILL_RE, (decl, prefix, token) => {
            const hex = toHex(token);
            return hex && map[hex] ? `${prefix}${map[hex]}` : decl;
        });
        return open + next + close;
    });
}

// All <path d="..."> strings in an SVG (used to build the garment clipPath).
export function parsePaths(svgText) {
    const paths = [];
    const re = /<path[^>]+\bd="([^"]+)"/g;
    let m;
    while ((m = re.exec(svgText)) !== null) paths.push(m[1]);
    return paths;
}
